/**
 * ═══════════════════════════════════════════════════════════════════
 *  TourTooltip — Positioned tooltip with step info & navigation
 * ═══════════════════════════════════════════════════════════════════
 *
 *  Renders a floating card with:
 *   - Step title & content
 *   - Step counter (2 / 10)
 *   - Back / Next / Skip buttons
 *   - Arrow pointer toward target element
 */

import React, { useRef, useEffect, useState } from 'react';
import { calculateTooltipPosition } from './utils';

// ── Tooltip dimensions ──────────────────────────────────────────────
const TOOLTIP_WIDTH = 320;
const TOOLTIP_MIN_HEIGHT = 120;

export default function TourTooltip({
  step,
  stepIndex,
  totalSteps,
  targetRect,
  onNext,
  onPrev,
  onSkip,
  isDark = false,
}) {
  const tooltipRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, placement: 'bottom' });

  // Recalculate position when target or step changes
  useEffect(() => {
    const height = tooltipRef.current?.offsetHeight || TOOLTIP_MIN_HEIGHT;
    const newPos = calculateTooltipPosition(
      targetRect,
      TOOLTIP_WIDTH,
      height,
      step?.placement || 'bottom'
    );
    setPos(newPos);
  }, [targetRect, step]);

  // Recalculate on resize
  useEffect(() => {
    const handleResize = () => {
      const height = tooltipRef.current?.offsetHeight || TOOLTIP_MIN_HEIGHT;
      const newPos = calculateTooltipPosition(
        targetRect,
        TOOLTIP_WIDTH,
        height,
        step?.placement || 'bottom'
      );
      setPos(newPos);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [targetRect, step]);

  if (!step) return null;

  const isFirst = stepIndex === 0;
  const isLast = stepIndex === totalSteps - 1;

  // ── Colors ──────────────────────────────────────────────────────
  const bg = isDark ? '#1e293b' : '#ffffff';
  const textPrimary = isDark ? '#f1f5f9' : '#1e293b';
  const textSecondary = isDark ? '#94a3b8' : '#64748b';
  const border = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)';
  const teal = '#0d9488';
  const tealHover = '#0f766e';
  const btnBg = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)';

  return (
    <div
      ref={tooltipRef}
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        width: TOOLTIP_WIDTH,
        zIndex: 99999,
        backgroundColor: bg,
        borderRadius: '0.75rem',
        border: `1px solid ${border}`,
        boxShadow: isDark
          ? '0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.05)'
          : '0 20px 60px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.03)',
        fontFamily: 'system-ui, sans-serif',
        transition: 'top 0.3s ease, left 0.3s ease, opacity 0.25s ease',
        opacity: 1,
        animation: 'tourTooltipIn 0.25s ease',
      }}
    >
      {/* ── Step counter ── */}
      <div style={{
        padding: '0.625rem 0.875rem 0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <span style={{
          fontSize: '0.6875rem',
          fontWeight: 600,
          color: teal,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
        }}>
          Step {stepIndex + 1} of {totalSteps}
        </span>
        {!isLast && (
          <button
            onClick={onSkip}
            style={{
              background: 'none',
              border: 'none',
              color: textSecondary,
              fontSize: '0.6875rem',
              cursor: 'pointer',
              padding: '0.125rem 0.25rem',
              borderRadius: '0.25rem',
              fontFamily: 'system-ui, sans-serif',
            }}
          >
            Skip tour
          </button>
        )}
      </div>

      {/* ── Title ── */}
      {step.title && (
        <div style={{
          padding: '0.5rem 0.875rem 0',
          fontSize: '0.875rem',
          fontWeight: 700,
          color: textPrimary,
          lineHeight: 1.3,
        }}>
          {step.title}
        </div>
      )}

      {/* ── Content ── */}
      <div style={{
        padding: '0.375rem 0.875rem 0.625rem',
        fontSize: '0.8125rem',
        color: textSecondary,
        lineHeight: 1.55,
      }}>
        {typeof step.content === 'function' ? step.content() : step.content}
      </div>

      {/* ── Navigation buttons ── */}
      <div style={{
        padding: '0 0.875rem 0.75rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: isFirst ? 'flex-end' : 'space-between',
        gap: '0.5rem',
      }}>
        {!isFirst && (
          <button
            onClick={onPrev}
            style={{
              background: btnBg,
              border: 'none',
              color: textSecondary,
              fontSize: '0.75rem',
              fontWeight: 600,
              padding: '0.375rem 0.75rem',
              borderRadius: '0.375rem',
              cursor: 'pointer',
              fontFamily: 'system-ui, sans-serif',
              transition: 'background 0.15s',
            }}
          >
            ← Back
          </button>
        )}

        <button
          onClick={onNext}
          style={{
            background: isLast
              ? `linear-gradient(135deg, ${teal}, #115e59)`
              : teal,
            border: 'none',
            color: '#fff',
            fontSize: '0.75rem',
            fontWeight: 600,
            padding: '0.375rem 0.875rem',
            borderRadius: '0.375rem',
            cursor: 'pointer',
            fontFamily: 'system-ui, sans-serif',
            transition: 'background 0.15s',
            letterSpacing: '0.01em',
          }}
          onMouseEnter={(e) => { e.target.style.background = isLast ? `linear-gradient(135deg, ${tealHover}, #134e4a)` : tealHover; }}
          onMouseLeave={(e) => { e.target.style.background = isLast ? `linear-gradient(135deg, ${teal}, #115e59)` : teal; }}
        >
          {isLast ? '✅ Finish Tour' : 'Next →'}
        </button>
      </div>

      {/* ── Progress bar ── */}
      <div style={{
        height: 3,
        borderRadius: '0 0 0.75rem 0.75rem',
        background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
        overflow: 'hidden',
      }}>
        <div style={{
          height: '100%',
          width: `${((stepIndex + 1) / totalSteps) * 100}%`,
          background: `linear-gradient(90deg, ${teal}, #14b8a6)`,
          borderRadius: '0 0 0.75rem 0.75rem',
          transition: 'width 0.4s ease',
        }} />
      </div>

      {/* ── Inline keyframes (injected once) ── */}
      <style>{`
        @keyframes tourTooltipIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
